/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import { AbstractRestService, arrayOf, identity, RestSettings, SlicedDataSource } from '@gh/core-data';
import { Observable } from 'rxjs';
import { ENV_BASE_URL } from '@gh/config';
import {
  CommunicationListItemDto,
  CommunicationListItemMapper,
  ActivityDetails,
  CommunicationMeetingMapper,
  CreateCommunicationActivityListItemMapper, GuestListItemDto, Assignee,
} from '../communications';
import { ContactCustomerListFilterDto } from '../contacts';

@Injectable()
export class ActivityRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  get(sfId: ID, meetingId: ID): Observable<ActivityDetails> {
    return this.httpGet(`${this.crm}/leads/sales-folders/${sfId}/activity/${meetingId}`, CommunicationMeetingMapper);
  }

  getList(sfId: ID): Observable<ActivityDetails[]> {
    return this.httpGet(`${this.crm}/leads/sales-folders/${sfId}/activity`, arrayOf(CommunicationMeetingMapper));
  }

  setState(sfId: ID, meetingId: ID, status: boolean): Observable<ActivityDetails[]> {
    return this.httpPut(`${this.crm}/leads/sales-folders/${sfId}/activity/${meetingId}/state`,
                        {
                          isDone: status,
                        },
                        identity(),
                        arrayOf(CommunicationMeetingMapper));
  }

  create(sfId: ID, meeting: ActivityDetails): Observable<ActivityDetails> {
    return this.httpPost(`${this.crm}/leads/sales-folders/${sfId}/activity`,
                         meeting,
                         CommunicationMeetingMapper,
                         CommunicationMeetingMapper);
  }

  leadCommunicationCreate(sfId: ID, meeting: ActivityDetails): Observable<CommunicationListItemDto> {
    return this.httpPost(`${this.crm}/leads/sales-folders/${sfId}/activity`,
                         meeting,
                         CommunicationMeetingMapper,
                         CommunicationListItemMapper);
  }

  changeStatus(sfId: ID, meetingId: ID): Observable<ActivityDetails[]> {
    return this.httpPut(`${this.crm}/leads/sales-folders/${sfId}/activity/${meetingId}/status`,
                        {
                             active : true,
                           },
                        identity(),
                        arrayOf(CommunicationMeetingMapper));
  }

  delete(sfId: ID, meetingId: ID): Observable<ActivityDetails[]> {
    return this.httpDelete(`${this.crm}/leads/sales-folders/${sfId}/activity/${meetingId}`,
                           {} as any,
                           identity(),
                           arrayOf(CommunicationMeetingMapper));
  }

  update(sfId: ID, meeting: ActivityDetails): Observable<CommunicationListItemDto> {
    const meetingId = meeting.id;
    return this.httpPut(`${this.crm}/leads/sales-folders/${sfId}/activity/${meetingId}`,
                        meeting,
                        CommunicationMeetingMapper,
                        CommunicationListItemMapper);
  }

  leadCommunicationUpdate(sfId: ID, meeting: ActivityDetails): Observable<CommunicationListItemDto> {
    const meetingId = meeting.id;
    return this.httpPut(`${this.crm}/leads/sales-folders/${sfId}/activity/${meetingId}`,
                        meeting,
                        CommunicationMeetingMapper,
                        CommunicationListItemMapper);
  }

  updateContactActivity(contactId: ID, meeting: ActivityDetails): Observable<CommunicationListItemDto> {
    const meetingId = meeting.id;
    return this.httpPut(`${this.crm}/contacts/${contactId}/communications/activity/${meetingId}`,
                        meeting,
                        CommunicationMeetingMapper,
                        CommunicationListItemMapper);
  }

  getContactActivity(contactId: ID, activityId: ID): Observable<ActivityDetails> {
    return this.httpGet(`${this.crm}/contacts/${contactId}/communications/activity/${activityId}`, CommunicationMeetingMapper);
  }

  createContactActivity(contactId: ID, meeting: any): Observable<CommunicationListItemDto> {
    return this.httpPost(`${this.crm}/contacts/${contactId}/communications/activity`,
                         meeting,
                         CreateCommunicationActivityListItemMapper,
                         CommunicationListItemMapper);
  }

  getJobActivity(jobId: ID, activityId: ID): Observable<ActivityDetails> {
    return this.httpGet(`${this.crm}/jobs/${jobId}/communications/activity/${activityId}`, CommunicationMeetingMapper);
  }

  createJobActivity(jobId: ID, meeting: any): Observable<CommunicationListItemDto> {
    return this.httpPost(`${this.crm}/jobs/${jobId}/communications/activity`,
                         meeting,
                         CommunicationMeetingMapper,
                         CommunicationListItemMapper);
  }

  updateJobActivity(jobId: ID, meeting: ActivityDetails): Observable<CommunicationListItemDto> {
    const meetingId = meeting.id;
    return this.httpPut(`${this.crm}/jobs/${jobId}/communications/activity/${meetingId}`,
                        meeting,
                        CommunicationMeetingMapper,
                        CommunicationListItemMapper);
  }

  findGuests(sfId: ID, filter: ContactCustomerListFilterDto, contactId?: ID): SlicedDataSource<GuestListItemDto> {
    let url = `${this.crm}/activity/guests?searchString=${filter.name}`;
    if (contactId) {
      url += `&contactId=${contactId}`;
    }
    if (sfId) {
      url += `&salesFolderId=${sfId}`;
    }
    return this.createSlicedDataSourceGet(url, identity());
  }

  findAssignee(filter: ContactCustomerListFilterDto): SlicedDataSource<Assignee> {
    return this.createSlicedDataSourceGet(`${this.crm}/activity/assignee?searchString=${filter.name}`,
                                          identity());
  }

  getCurrentAssignee(): any {
    return this.httpGet(`${this.crm}/activity/current-assignee`, identity());
  }
}
